import { type QuoteRequest, type PolicyUpdatesRequest, type PolicyUpdateResponse } from './InsuranceTypes'
import ServiceBase from './ServiceBase'

class InsuranceService extends ServiceBase {
  async getQuotes(request: QuoteRequest, displayCurrency?: string): Promise<any[] | any> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/get_quotes`
      this.webPost(
        {
          params: JSON.stringify(request),
          display_currency: displayCurrency ?? ''
        },
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        },
        5000,
        3
      )
    })
  }

  async updateQuotes(request: QuoteRequest, orderId: number, insuranceId: number, insuranceOrderNumber: string, displayCurrency?: string): Promise<any[] | any> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/QuotePolicyChanges`
      this.webPost(
        {
          params: JSON.stringify(request),
          order_id: orderId,
          insurance_id: insuranceId,
          ins_order_number: insuranceOrderNumber,
          display_currency: displayCurrency ?? ''
        },
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        },
        5000,
        3
      )
    })
  }

  async getByOrderId(orderId: any): Promise<any[]> {
    const url = `api/insurance/get_by_order_id&orderId=${orderId}`
    return new Promise((resolve, reject) => {
      this.webGet(
        {},
        (res) => {
          resolve(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [])
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  async getConfigs(): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/get_configs`
      this.webGet(
        {},
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  async purchaseQuotes(request: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/purchase_quotes`
      this.webPost(
        {
          params: JSON.stringify(request)
        },
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  async updatePurchaseQuotes(request: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/CommitPolicyChanges`
      this.webPost(
        {
          params: JSON.stringify(request)
        },
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  async cancelPolicy(request: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/CancelPolicyOrOrder`
      this.webPost(
        {
          params: JSON.stringify(request)
        },
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  async getCountryAndZoneCodes(countryIds?: number[], zoneIds?: number[]) {
    const countryIdsStr = countryIds ? 'country_ids=' + countryIds.join(',') : ''
    const zoneIdsStr = zoneIds ? 'zone_ids=' + zoneIds.join(',') : ''
    return new Promise((resolve, reject) => {
      const url = `api/insurance/get_country_and_zone_codes&` + countryIdsStr + '&' + zoneIdsStr
      this.webGet(
        {},
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  async getCountryCodesByCityIds(cityIds): Promise<{ country_code: string }[]> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/get_country_codes_by_city_ids&city_ids=` + cityIds.join(',')
      this.webGet(
        {},
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  async getInfoListByOrder($orderId): Promise<any> {
    return new Promise((resove, reject) => {
      const url = 'api/insurance/get_info_list_by_order&order_id=' + $orderId
      this.webGet(
        {},
        (res) => {
          resove(res)
        },
        url,
        (err) => {
          reject(err)
        }
      )
    })
  }

  /**
   * Update an existing insurance policy
   * @param request PolicyUpdatesRequest containing the updates to commit
   * @returns Promise<PolicyUpdateResponse>
   *
   * Response codes:
   * - Success: Update completed successfully
   * - InvalidPaymentMethod: Payment method was not valid or missing
   * - AgencyCodeRequired: Valid AgencyCode required for subordinate organizations
   * - InvalidAgencyCode: Submitted AgencyCode is not valid
   * - PriceValidationError: Total doesn't match Arch rated total
   * - PolicyHasFiledClaim: Policy has a claim, no more updates allowed
   */
  async updatePolicy(request: PolicyUpdatesRequest): Promise<PolicyUpdateResponse> {
    return new Promise((resolve, reject) => {
      const url = `api/insurance/update_policy`
      this.webPost(
        {
          params: JSON.stringify(request)
        },
        (res) => {
          resolve(res)
        },
        url,
        (err) => {
          reject(err)
        },
        5000,
        3
      )
    })
  }

  // real_depart_date/real_return_date only - fetched fresh right before a policy update commit so
  // it reflects the order's current option/order_related date shifts, not whatever was cached at
  // page load (account/order/info can be called long before the user clicks "Update Insurance").
  async getOrderRealDates(orderId: number): Promise<{ real_depart_date?: string; real_return_date?: string }> {
    return new Promise((resolve, reject) => {
      this.webPost(
        { order_id: orderId, type: 0 },
        (res) => {
          resolve(res || {})
        },
        'account/order/info',
        (err) => {
          reject(err)
        },
        5000,
        3
      )
    })
  }
}

export default new InsuranceService()
